import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useApi } from '@/hooks/useApi';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, UserCog } from 'lucide-react';

const schema = z.object({
  name: z.string().min(3, 'الاسم يجب أن يكون 3 أحرف على الأقل'),
  phone: z.string().min(1, 'رقم الهاتف مطلوب').regex(/^\+?[0-9]{9,15}$/, 'صيغة رقم الهاتف غير صحيحة'),
  email: z.string().email('صيغة البريد الإلكتروني غير صحيحة').optional().or(z.literal('')),
  capacity_id: z.string().optional(),
  payment_type: z.enum(['salary', 'commission']),
  commission_rate: z.string().optional().refine((val) => {
    if (!val) return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0 && num <= 100;
  }, { message: 'نسبة العمولة يجب أن تكون بين 0 و 100' }),
  per_trip_bonus: z.string().optional().refine((val) => {
    if (!val) return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, { message: 'حافز الرحلة يجب أن يكون رقماً موجباً' }),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface Capacity { id: string; name: string; vehicle_type: string; }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  driver: {
    id: string;
    name: string;
    phone: string;
    email?: string;
    capacity_id?: string;
    payment_type?: string;
    commission_rate?: number;
    per_trip_bonus?: number;
    is_active: boolean;
  } | null;
}

export function EditDriverModal({ open, onOpenChange, onSuccess, driver }: Props) {
  const { patch, get, loading } = useApi();
  const [capacities, setCapacities] = useState<Capacity[]>([]);
  const [loadingCapacities, setLoadingCapacities] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '', phone: '', email: '', capacity_id: '',
      payment_type: 'salary', commission_rate: '', per_trip_bonus: '0', is_active: true,
    },
  });

  const paymentType = form.watch('payment_type');

  // Pre-fill form when driver changes
  useEffect(() => {
    if (driver && open) {
      form.reset({
        name: driver.name || '',
        phone: driver.phone || '',
        email: driver.email || '',
        capacity_id: driver.capacity_id || '',
        payment_type: (driver.payment_type as 'salary' | 'commission') || 'salary',
        commission_rate: driver.commission_rate ? String(driver.commission_rate * 100) : '',
        per_trip_bonus: driver.per_trip_bonus !== undefined ? String(driver.per_trip_bonus) : '0',
        is_active: driver.is_active ?? true,
      });
    }
  }, [driver, open, form]);

  useEffect(() => {
    if (open) {
      setLoadingCapacities(true);
      get('/api/v1/capacities').then((res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        setCapacities(data);
      }).catch(() => {}).finally(() => setLoadingCapacities(false));
    }
  }, [open]);

  const onSubmit = async (values: FormValues) => {
    if (!driver) return;
    try {
      const payload: Record<string, any> = {
        name: values.name.trim(),
        phone: values.phone.trim(),
        is_active: values.is_active,
        payment_type: values.payment_type,
      };
      if (values.email) payload.email = values.email.trim();
      if (values.capacity_id && values.capacity_id !== 'none') payload.capacity_id = values.capacity_id;
      if (values.payment_type === 'commission' && values.commission_rate) {
        payload.commission_rate = parseFloat(values.commission_rate) / 100;
      }
      if (values.per_trip_bonus !== undefined && values.per_trip_bonus !== '') {
        payload.per_trip_bonus = parseFloat(values.per_trip_bonus);
      }

      await patch(`/api/v1/drivers/${driver.id}`, payload);
      toast.success('تم تحديث بيانات السائق بنجاح');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'فشل في تحديث بيانات السائق');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <UserCog className="h-5 w-5" />
            تعديل بيانات السائق
          </DialogTitle>
          <DialogDescription>تعديل المعلومات الأساسية للسائق</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">المعلومات الأساسية</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>الاسم الكامل *</FormLabel>
                    <FormControl><Input placeholder="اسم السائق" {...field} disabled={loading} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>رقم الهاتف *</FormLabel>
                    <FormControl><PhoneInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} disabled={loading} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem>
                  <FormLabel>البريد الإلكتروني</FormLabel>
                  <FormControl><Input type="email" dir="ltr" className="text-start" placeholder="driver@example.com" {...field} disabled={loading} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            {/* Capacity */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">سعة المركبة</h3>
              <FormField control={form.control} name="capacity_id" render={({ field }) => (
                <FormItem>
                  <FormLabel>نوع السعة</FormLabel>
                  <Select onValueChange={(v) => field.onChange(v === 'none' ? '' : v)} value={field.value || 'none'} disabled={loading || loadingCapacities}>
                    <FormControl><SelectTrigger><SelectValue placeholder="اختر نوع السعة" /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="none">بدون تحديد</SelectItem>
                      {capacities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            {/* Payment */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">معلومات الدفع</h3>
              <FormField control={form.control} name="payment_type" render={({ field }) => (
                <FormItem>
                  <FormLabel>نوع الدفع</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value} className="flex gap-6" disabled={loading}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="salary" id="edit-salary" />
                        <Label htmlFor="edit-salary" className="cursor-pointer">راتب ثابت</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="commission" id="edit-commission" />
                        <Label htmlFor="edit-commission" className="cursor-pointer">عمولة</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {paymentType === 'commission' && (
                <FormField control={form.control} name="commission_rate" render={({ field }) => (
                  <FormItem>
                    <FormLabel>نسبة العمولة (%) *</FormLabel>
                    <FormControl><Input type="number" placeholder="15" min={0} max={100} step={0.1} dir="ltr" className="text-start" {...field} disabled={loading} /></FormControl>
                    <FormDescription>أدخل النسبة المئوية (مثال: 15 تعني 15%)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
              <FormField control={form.control} name="per_trip_bonus" render={({ field }) => (
                <FormItem>
                  <FormLabel>حافز الرحلة</FormLabel>
                  <FormControl><Input type="number" placeholder="0" min={0} step={0.01} dir="ltr" className="text-start" {...field} disabled={loading} /></FormControl>
                  <FormDescription>مبلغ ثابت يضاف لكل رحلة</FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">الحالة</h3>
              <FormField control={form.control} name="is_active" render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>نشط</FormLabel>
                    <FormDescription>السائقين غير النشطين لا يمكنهم استلام شحنات جديدة</FormDescription>
                  </div>
                  <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} disabled={loading} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin ms-2" />جاري الحفظ...</> : 'حفظ التعديلات'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>إلغاء</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
